import React, {useState, useEffect} from 'react';

import styled from 'styled-components';

import {ICoach} from '@stryd/models';
import {dimensions} from '@stryd/react-ui';
import {getPxValueFromString} from '@stryd/util-lib';

import CoachesGrid from './CoachesGrid';
import SelectedCoach from './SelectedCoach';

const breakpointDesktopLarge = '2400px';

const breakpointOptions = {
  mobile: 'mobile',
  tabletPortrait: 'tabletPortrait',
  tabletLandscape: 'tabletLandscape',
  desktopSmall: 'desktopSmall',
  desktopLarge: 'desktopLarge',
};

const entriesPerRow = {
  noSelectedCoach: {
    [breakpointOptions.mobile]: 1,
    [breakpointOptions.tabletPortrait]: 2,
    [breakpointOptions.tabletLandscape]: 3,
    [breakpointOptions.desktopSmall]: 4,
    [breakpointOptions.desktopLarge]: 5,
  },
  selectedCoach: {
    [breakpointOptions.mobile]: 1,
    [breakpointOptions.tabletPortrait]: 1,
    [breakpointOptions.tabletLandscape]: 2,
    [breakpointOptions.desktopSmall]: 3,
    [breakpointOptions.desktopLarge]: 4,
  },
};

const selectedCoachWidth = {
  [breakpointOptions.mobile]: '100%',
  [breakpointOptions.tabletPortrait]: '50%',
  [breakpointOptions.tabletLandscape]: '45%',
  [breakpointOptions.desktopSmall]: '35%',
  [breakpointOptions.desktopLarge]: '30%',
};

const Container = styled.section`
  width: 100%;
  height: auto;
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: flex-start;
`;

const ListContainer = styled.div`
  flex: 1;
`;

type SelectedCoachContainerProps = {
  containerWidth: string;
  stickAt: string;
};

const SelectedCoachContainer = styled.div<SelectedCoachContainerProps>`
  width: ${(props) => props.containerWidth};
  height: calc(100vh - ${(props) => props.stickAt});
  overflow-x: hidden;
  overflow-y: auto;
  background: black;
  position: sticky;
  top: ${(props) => props.stickAt};
  color: white;
  @media screen and (max-width: ${dimensions.breakpoints.mobile}) {
    height: 100vh;
    position: fixed;
    z-index: 100;
    top: 0;
    left: 0;
  }
`;

type CoachesListProps = {
  data: ICoach[];
  stickAt: string;
};

const CoachesList: React.FC<React.PropsWithChildren<CoachesListProps>> = ({
  data,
  stickAt,
}) => {
  const [activeCoach, setActiveCoach] = useState<ICoach | null>(null);
  const [breakpoint, setBreakPoint] = useState(breakpointOptions.desktopSmall);
  const [scrollPosition, setScrollPosition] = useState<number | null>(null);

  useEffect(() => {
    const handleListResize = () => {
      let newBreakPoint = breakpointOptions.desktopSmall;
      if (
        window.innerWidth <=
        parseInt(getPxValueFromString(dimensions.breakpoints.mobile))
      ) {
        newBreakPoint = breakpointOptions.mobile;
      } else if (
        window.innerWidth <=
        parseInt(getPxValueFromString(dimensions.breakpoints.tabletPortrait))
      ) {
        newBreakPoint = breakpointOptions.tabletPortrait;
      } else if (
        window.innerWidth <=
        parseInt(getPxValueFromString(dimensions.breakpoints.tabletLandscape))
      ) {
        newBreakPoint = breakpointOptions.tabletLandscape;
      } else if (
        window.innerWidth >=
        parseInt(getPxValueFromString(breakpointDesktopLarge))
      ) {
        newBreakPoint = breakpointOptions.desktopLarge;
      }

      if (newBreakPoint !== breakpoint) {
        setBreakPoint(newBreakPoint);
      }
    };

    handleListResize();

    window.addEventListener('resize', handleListResize);
    return () => {
      window.removeEventListener('resize', handleListResize);
    };
  }, [breakpoint]);

  useEffect(() => {
    setActiveCoach(null);
  }, [data]);

  useEffect(() => {
    if (breakpoint === breakpointOptions.mobile && !activeCoach) {
      window.scrollTo(0, scrollPosition || 0);
      setScrollPosition(null);
    }
  }, [activeCoach, breakpoint, scrollPosition]);

  const coachesPerRow = activeCoach
    ? entriesPerRow.selectedCoach[breakpoint]
    : entriesPerRow.noSelectedCoach[breakpoint];

  const selectedCoachContainerWidth = activeCoach
    ? selectedCoachWidth[breakpoint]
    : '';

  return (
    <Container>
      <ListContainer>
        <CoachesGrid
          activeCoach={activeCoach}
          coaches={data}
          coachesPerRow={coachesPerRow}
          onCoachClick={(coach: ICoach) => {
            if (!activeCoach || coach.email !== activeCoach.email) {
              setActiveCoach(coach);
              if (breakpoint === breakpointOptions.mobile) {
                setScrollPosition(window.scrollY);
              }
            } else {
              setActiveCoach(null);
            }
          }}
        />
      </ListContainer>
      {activeCoach && (
        <SelectedCoachContainer
          stickAt={stickAt}
          containerWidth={selectedCoachContainerWidth}
        >
          <SelectedCoach
            coachData={activeCoach}
            handleExitClick={() => {
              setActiveCoach(null);
            }}
          />
        </SelectedCoachContainer>
      )}
    </Container>
  );
};

export default CoachesList;
